import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Menu, X } from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'
import Logo from './Logo'
import ApplicationsDropdown from './ApplicationsDropdown'
import ServicesDropdown from './ServicesDropdown'

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const location = useLocation()

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const navItems = [
    { name: 'Home', href: '/', isHash: false },
  ]

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-background/95 backdrop-blur-md shadow-lg' 
          : 'bg-transparent'
      }`}
    >
      <div className="w-full relative">
        <div className="flex items-center justify-between h-20 md:h-36 pl-24 md:pl-48 pr-4 sm:pr-6 lg:pr-8">
          {/* Logo - positioned absolutely to the left edge */}
          <div className="absolute left-0 top-1/2 transform -translate-y-1/2 flex items-center">
            <Logo variant="original" size="3xl" />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8 ml-auto">
            {navItems.map((item, index) => (
              <motion.div key={item.name} whileHover={{ y: -2 }}>
                <Link
                  to={item.href}
                  className="text-secondary hover:text-primary transition-colors duration-300 font-medium"
                >
                  {item.name}
                </Link>
              </motion.div>
            ))}
            <motion.div whileHover={{ y: -2 }}>
              <ApplicationsDropdown />
            </motion.div>
            <motion.div whileHover={{ y: -2 }}>
              <ServicesDropdown />
            </motion.div>
          </nav>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-secondary hover:text-primary transition-colors ml-auto"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-background/95 backdrop-blur-md border-t border-gray-800"
          >
            <div className="py-4 space-y-4 px-4">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className="block text-secondary hover:text-primary transition-colors duration-300 font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              
              <div className="space-y-2">
                <div className="text-secondary font-medium mb-2">Applications</div>
                <a
                  href="https://cloud-chekr.modumatic.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-secondary hover:text-primary transition-colors duration-300 pl-4"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Cloud Chekr
                </a>
              </div>
              
              <div className="space-y-2">
                <div className="text-secondary font-medium mb-2">Services</div>
                <Link
                  to="/services/devops"
                  className="block text-secondary hover:text-primary transition-colors duration-300 pl-4"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  DevOps
                </Link>
                <Link
                  to="/services/custom-software-development"
                  className="block text-secondary hover:text-primary transition-colors duration-300 pl-4"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Custom Software Development
                </Link>
                <Link
                  to="/services/architecture"
                  className="block text-secondary hover:text-primary transition-colors duration-300 pl-4"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Architecture
                </Link>
              </div>
            </div>
          </motion.nav>
        )}
      </div>
    </motion.header>
  )
}

export default Header
